import { useEffect, useMemo, useState, Fragment } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import StatusChip from "@/components/StatusChip";
import { VersionDiffPanel } from "@/components/VersionDiff";
import { ArrowLeft, Plus, Save, Trash2, GitCommit, Send, Check, X, RotateCcw, Cog, FileDown, GripVertical, ChevronDown, ChevronRight, AlertTriangle, Undo2, Copy, ArrowRightCircle, FlaskConical, Factory, Layers } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { API_BASE } from "@/lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

// ---- helpers ----
function computeRow(op, cpmMachine, cpmTable) {
  // `op.sam` now holds CT in seconds — divide by 60 to convert to minutes
  // before multiplying CPM (₹ per minute). Field name preserved for
  // backward-compat with historical DB documents and Excel exports.
  const ct_sec = Number(op.sam || 0);
  const isTable = op.machine === "Table Operation" || op.machine === "Table Operator";
  const cpm = isTable ? Number(cpmTable || 0) : Number(cpmMachine || 0);
  const sam_cost = (ct_sec / 60) * cpm;
  const num = Number(op.num_operations || 0);
  const price = Number(op.price_per_operation || 0);
  const op_cost = num * price;
  return { sam_cost, op_cost };
}

// ---- Combobox: searchable dropdown for machine + operation, with optional "add new" ----
function Combobox({ value, onChange, options, placeholder = "Select…", disabled, testid, emptyText = "No results.", onAddNew, addNewLabel = "Add" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const trimmed = query.trim();
  const exactMatch = trimmed && options.some((o) => o.toLowerCase() === trimmed.toLowerCase());
  const showAdd = !!onAddNew && trimmed.length > 0 && !exactMatch && !disabled;
  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setQuery(""); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testid}
          className="w-full h-8 px-2 rounded border border-transparent hover:border-emerald-200 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-100 bg-transparent focus:bg-white text-[13px] outline-none text-left flex items-center gap-1 disabled:cursor-not-allowed"
        >
          <span className={`flex-1 truncate ${!value ? "text-slate-400" : "text-slate-900"}`}>{value || placeholder}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[280px]" align="start">
        <Command shouldFilter>
          <CommandInput
            placeholder="Search…"
            className="h-9 text-[13px]"
            data-testid={`${testid}-search`}
            value={query}
            onValueChange={setQuery}
          />
          <CommandList className="max-h-64">
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt}
                  value={opt}
                  onSelect={() => { onChange(opt); setOpen(false); setQuery(""); }}
                  className="text-[13px] cursor-pointer"
                >
                  {opt}
                </CommandItem>
              ))}
            </CommandGroup>
            {showAdd && (
              <CommandGroup heading="New">
                <CommandItem
                  value={`__add__${trimmed}`}
                  onSelect={async () => {
                    await onAddNew(trimmed);
                    setOpen(false); setQuery("");
                  }}
                  data-testid={`${testid}-add-new`}
                  className="text-[13px] text-brand cursor-pointer font-medium"
                >
                  <Plus className="h-3.5 w-3.5 mr-1.5" /> {addNewLabel} "{trimmed}"
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ---- main ----
export default function BulletinDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [b, setB] = useState(null);
  const [ops, setOps] = useState([]);
  const [cpmMachine, setCpmMachine] = useState(6.0);
  const [cpmTable, setCpmTable] = useState(3.5);
  const [machines, setMachines] = useState([]);
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [approvalAction, setApprovalAction] = useState("approve");
  const [approvalComment, setApprovalComment] = useState("");
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareVer, setCompareVer] = useState(null);
  const [expandedVersion, setExpandedVersion] = useState(null);   // int | null
  const [diffAgainst, setDiffAgainst] = useState("current");      // "current" | "previous"
  // Drag & drop for reordering rows
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  // CPM change alert (shown after new_version if master CPM drifted)
  const [cpmAlertOpen, setCpmAlertOpen] = useState(false);
  const [cpmAlertData, setCpmAlertData] = useState(null);
  // Duplicate as new style
  const [dupOpen, setDupOpen] = useState(false);
  const [dupForm, setDupForm] = useState({ buyer: "", our_style: "", buyer_style: "" });
  const [dupSaving, setDupSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/bulletins/${id}`);
    setB(data);
    setOps(data.operations || []);
    setCpmMachine(data.cpm_machine ?? 6.0);
    setCpmTable(data.cpm_table ?? 3.5);
  };
  useEffect(() => {
    load();
    api.get("/masters/machines").then((r) => setMachines(r.data));
    // eslint-disable-next-line
  }, [id]);

  const totals = useMemo(() => {
    let sam = 0, opc = 0, totalSam = 0;
    ops.forEach((o) => {
      const c = computeRow(o, cpmMachine, cpmTable);
      sam += c.sam_cost; opc += c.op_cost;
      totalSam += Number(o.sam || 0);
    });
    return { sam_cost_total: sam, op_cost_total: opc, total_sam: totalSam };
  }, [ops, cpmMachine, cpmTable]);

  if (!b) return <div className="text-slate-500 text-sm">Loading…</div>;

  const isManagement = (user?.role === "admin" || user?.role === "admin_addl") || user?.role === "management";
  const isAdmin = (user?.role === "admin" || user?.role === "admin_addl");
  const canEdit = !["Approved", "Released"].includes(b.status);

  const machineOperations = (machineName) => machines.find((m) => m.name === machineName)?.operations || [];
  const machineIdByName = (machineName) => machines.find((m) => m.name === machineName)?.id;

  const addOpToMachine = async (rowIdx, machineName, operationName) => {
    const mid = machineIdByName(machineName);
    if (!mid) { toast.error("Machine not found"); return; }
    try {
      const { data } = await api.post(`/masters/machines/${mid}/operations`, { operation_name: operationName });
      // Update local machines list
      setMachines((prev) => prev.map((m) => (m.id === mid ? data : m)));
      // Select the new operation for the row
      patchOp(rowIdx, { operation_name: operationName });
      toast.success(`Added "${operationName}" to ${machineName}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Add failed");
    }
  };

  const addOp = () => setOps([
    ...ops,
    { seq: ops.length + 1, machine: "", operation_name: "", sam: 0, num_operations: 1, price_per_operation: 0, tools_guides: "" },
  ]);

  const removeOp = (i) => setOps(ops.filter((_, idx) => idx !== i).map((o, idx) => ({ ...o, seq: idx + 1 })));

  const onDragStart = (i) => (e) => {
    if (!canEdit) return;
    setDragIdx(i);
    try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", String(i)); } catch {}
  };
  const onDragOver = (i) => (e) => {
    if (!canEdit || dragIdx === null) return;
    e.preventDefault();
    if (dragOverIdx !== i) setDragOverIdx(i);
    try { e.dataTransfer.dropEffect = "move"; } catch {}
  };
  const onDrop = (i) => (e) => {
    if (!canEdit || dragIdx === null) return;
    e.preventDefault();
    if (dragIdx === i) { setDragIdx(null); setDragOverIdx(null); return; }
    const next = [...ops];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(i, 0, moved);
    setOps(next.map((o, idx) => ({ ...o, seq: idx + 1 })));
    setDragIdx(null); setDragOverIdx(null);
  };
  const onDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };

  const patchOp = (i, patch) => {
    setOps((cur) => cur.map((o, idx) => {
      if (idx !== i) return o;
      const next = { ...o, ...patch };
      // If machine changes, reset operation_name if not in that machine's list
      if ("machine" in patch) {
        const validOps = machines.find((m) => m.name === patch.machine)?.operations || [];
        if (!validOps.includes(next.operation_name)) next.operation_name = "";
      }
      return next;
    }));
  };

  const save = async () => {
    try {
      await api.patch(`/bulletins/${id}`, { operations: ops, cpm_machine: Number(cpmMachine), cpm_table: Number(cpmTable) });
      toast.success("Bulletin saved"); load();
    } catch { toast.error("Save failed"); }
  };

  const submitForApproval = async () => {
    await api.patch(`/bulletins/${id}`, { operations: ops, cpm_machine: Number(cpmMachine), cpm_table: Number(cpmTable), status: "Ready for Approval" });
    toast.success("Sent for approval"); load();
  };

  const newVersion = async () => {
    try {
      const { data } = await api.post(`/bulletins/${id}/new_version`);
      toast.success(`New version v${data.bulletin.version} created`);
      if (data.cpm_changed) { setCpmAlertData(data); setCpmAlertOpen(true); }
      load();
    } catch (e) { toast.error("Failed to create version"); }
  };

  const restoreVersion = async (v) => {
    if (!window.confirm(`Restore v${v}? This will create a new draft version with the operations from v${v}.`)) return;
    try {
      const { data } = await api.post(`/bulletins/${id}/restore_version/${v}`);
      toast.success(`Restored v${v} as new v${data.bulletin.version}`);
      if (data.cpm_changed) { setCpmAlertData(data); setCpmAlertOpen(true); }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
  };

  const doApproval = async () => {
    await api.post(`/bulletins/${id}/approve`, { action: approvalAction, comment: approvalComment });
    toast.success(`Bulletin ${approvalAction}d`); setApprovalOpen(false); setApprovalComment(""); load();
  };

  const openDuplicate = () => {
    setDupForm({ buyer: "", our_style: "", buyer_style: "" });
    setDupOpen(true);
  };

  const doDuplicate = async () => {
    const buyer = dupForm.buyer.trim();
    const our_style = dupForm.our_style.trim();
    if (!buyer || !our_style) { toast.error("Buyer and Our Style Number are required"); return; }
    setDupSaving(true);
    try {
      const { data } = await api.post(`/bulletins/${id}/duplicate`, {
        buyer, our_style, buyer_style: dupForm.buyer_style.trim(),
      });
      toast.success(`Duplicated as new draft ${data.our_style}`);
      setDupOpen(false);
      nav(`/bulletins/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Duplicate failed");
    }
    setDupSaving(false);
  };

  const transferToProduction = async () => {
    if (!window.confirm(`Transfer ${b.our_style} to Production OB? A new Production draft will be created with the same operations and rates.`)) return;
    try {
      const { data } = await api.post(`/bulletins/${id}/transfer-to-production`);
      toast.success(`Transferred to Production OB (${data.our_style})`);
      nav(`/bulletins/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transfer failed");
    }
  };

  const comparisonRows = compareVer ? computeCompare(compareVer.operations || [], ops, compareVer.cpm_machine ?? 6, compareVer.cpm_table ?? 3.5, cpmMachine, cpmTable) : [];

  return (
    <div className="space-y-6" data-testid="bulletin-detail-page">
      {/* Header */}
      <div>
        <button onClick={() => nav("/bulletins")} className="inline-flex items-center gap-1.5 text-[12.5px] text-slate-500 hover:text-slate-800 mb-3">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Bulletins
        </button>
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-heading font-bold text-3xl tracking-tight">{b.our_style}</h1>
              <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-[12px] font-mono">v{b.version}</span>
              <StatusChip status={b.status} />
              {(() => {
                const stg = b.stage || "pp_gold_seal";
                const isProd = stg === "production";
                const Icon = isProd ? Factory : FlaskConical;
                return (
                  <span
                    data-testid="bulletin-stage-badge"
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11.5px] font-semibold ${isProd ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-brand-soft text-brand border border-rose-200"}`}
                  >
                    <Icon className="h-3.5 w-3.5" /> {isProd ? "Production OB" : "PP / Gold Seal OB"}
                  </span>
                );
              })()}
            </div>
            <div className="mt-2 text-slate-600 text-[14px]">{b.buyer} · Buyer Style: <span className="font-semibold text-slate-900">{b.buyer_style || "—"}</span></div>
            {Array.isArray(b.our_styles) && b.our_styles.length > 1 && (
              <div className="mt-2 flex items-center gap-1.5 flex-wrap text-[12px]" data-testid="bulletin-colourway-chips">
                <span className="text-slate-500 uppercase tracking-wide text-[10.5px] font-semibold">Colourways clubbed:</span>
                {b.our_styles.map((s) => (
                  <span key={s} className={`inline-flex items-center px-2 py-0.5 rounded font-mono text-[11.5px] ${s === b.our_style ? "bg-brand text-white" : "bg-rose-50 text-brand"}`}>{s}</span>
                ))}
              </div>
            )}
            {b.transferred_from?.bulletin_id && (
              <div className="mt-1.5 text-[12px] text-slate-500 inline-flex items-center gap-1.5" data-testid="linked-from-source">
                <ArrowRightCircle className="h-3.5 w-3.5 text-emerald-600" />
                Transferred from PP / Gold Seal OB
                <button onClick={() => nav(`/bulletins/${b.transferred_from.bulletin_id}`)} className="font-mono text-brand hover:underline">{b.transferred_from.our_style}</button>
              </div>
            )}
            {b.transferred_to?.bulletin_id && (
              <div className="mt-1.5 text-[12px] text-slate-500 inline-flex items-center gap-1.5" data-testid="linked-to-target">
                <ArrowRightCircle className="h-3.5 w-3.5 text-emerald-600" />
                Transferred to Production OB
                <button onClick={() => nav(`/bulletins/${b.transferred_to.bulletin_id}`)} className="font-mono text-brand hover:underline">Open →</button>
              </div>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="bulletin-pdf-btn"><FileDown className="h-4 w-4 mr-2" /> Export PDF <ChevronDown className="h-4 w-4 ml-1.5 opacity-70" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel className="text-[11px] font-normal text-slate-500">Choose a PDF variant</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="bulletin-pdf-full-btn"
                  onSelect={async () => {
                    try {
                      const res = await fetch(`${API_BASE}/bulletins/${id}/export/pdf?variant=full`, { credentials: "include" });
                      if (!res.ok) throw new Error("Failed");
                      const blob = await res.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a"); a.href = url; a.download = `OB_${b.our_style}_v${b.version}.pdf`; a.click();
                      URL.revokeObjectURL(url);
                      toast.success("Full OB downloaded");
                    } catch { toast.error("PDF export failed"); }
                  }}
                >
                  <div className="flex flex-col gap-0.5 py-0.5">
                    <span className="text-[13px] font-medium">Full OB</span>
                    <span className="text-[11px] text-slate-500">Includes CPM, SAM Cost, Op Price, Op Total</span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="bulletin-pdf-working-btn"
                  onSelect={async () => {
                    try {
                      const res = await fetch(`${API_BASE}/bulletins/${id}/export/pdf?variant=working`, { credentials: "include" });
                      if (!res.ok) throw new Error("Failed");
                      const blob = await res.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a"); a.href = url; a.download = `Working_OB_${b.our_style}_v${b.version}.pdf`; a.click();
                      URL.revokeObjectURL(url);
                      toast.success("Working OB downloaded");
                    } catch { toast.error("PDF export failed"); }
                  }}
                >
                  <div className="flex flex-col gap-0.5 py-0.5">
                    <span className="text-[13px] font-medium">Working OB</span>
                    <span className="text-[11px] text-slate-500">Team-safe · no costing (CPM / CT Cost / Op Price / Op Total)</span>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" onClick={openDuplicate} data-testid="bulletin-duplicate-btn"><Copy className="h-4 w-4 mr-2" /> Duplicate as new style</Button>
            {(b.stage || "pp_gold_seal") === "pp_gold_seal" && b.status === "Approved" && !b.transferred_to && (
              <Button onClick={transferToProduction} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="transfer-to-production-btn">
                <ArrowRightCircle className="h-4 w-4 mr-2" /> Transfer to Production
              </Button>
            )}
            {canEdit && (
              <>
                <Button variant="outline" onClick={save} data-testid="bulletin-save-btn"><Save className="h-4 w-4 mr-2" /> Save</Button>
                <Button onClick={submitForApproval} className="bg-brand hover:bg-brand-dark text-white" data-testid="bulletin-submit-btn"><Send className="h-4 w-4 mr-2" /> Submit for Approval</Button>
              </>
            )}
            {b.status === "Approved" && (
              <Button variant="outline" onClick={newVersion} data-testid="bulletin-newversion-btn"><GitCommit className="h-4 w-4 mr-2" /> New Version</Button>
            )}
            {isManagement && b.status === "Ready for Approval" && (
              <Dialog open={approvalOpen} onOpenChange={setApprovalOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="bulletin-review-btn"><Check className="h-4 w-4 mr-2" /> Review</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Approve Bulletin</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-2">
                      <button onClick={() => setApprovalAction("approve")} className={`p-2.5 rounded-md text-[12.5px] font-medium border ${approvalAction === "approve" ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "border-slate-200"}`}><Check className="h-4 w-4 inline mr-1" /> Approve</button>
                      <button onClick={() => setApprovalAction("send_back")} className={`p-2.5 rounded-md text-[12.5px] font-medium border ${approvalAction === "send_back" ? "bg-amber-50 border-amber-300 text-amber-800" : "border-slate-200"}`}><RotateCcw className="h-4 w-4 inline mr-1" /> Send back</button>
                      <button onClick={() => setApprovalAction("reject")} className={`p-2.5 rounded-md text-[12.5px] font-medium border ${approvalAction === "reject" ? "bg-red-50 border-red-300 text-red-800" : "border-slate-200"}`}><X className="h-4 w-4 inline mr-1" /> Reject</button>
                    </div>
                    <Textarea placeholder="Add comment…" value={approvalComment} onChange={(e) => setApprovalComment(e.target.value)} rows={3} />
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setApprovalOpen(false)}>Cancel</Button>
                    <Button onClick={doApproval} className="bg-brand hover:bg-brand-dark text-white" data-testid="bulletin-approval-confirm">Confirm</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </div>

      {/* CPM strip + INDEPENDENT totals — CPMs are Admin-only and default from Master */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <Card className="p-4 rounded-md border-slate-200 bg-white">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-md bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0"><Cog className="h-4.5 w-4.5" /></div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">Machine Op CPM</div>
                {!isAdmin && <span className="text-[9.5px] uppercase tracking-wider text-slate-400 font-mono">Master</span>}
              </div>
              <input
                type="number" step="0.01"
                value={cpmMachine}
                disabled={!canEdit || !isAdmin}
                onChange={(e) => setCpmMachine(Number(e.target.value))}
                data-testid="cpm-machine-input"
                className="mt-1 w-full font-mono font-bold text-lg outline-none bg-transparent focus:bg-slate-50 rounded px-1 py-0.5 disabled:cursor-not-allowed"
              />
              <div className="text-[10.5px] text-slate-500">₹/min · {isAdmin ? "editable by admin" : "read-only · admin managed"}</div>
            </div>
          </div>
        </Card>
        <Card className="p-4 rounded-md border-slate-200 bg-white">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-md bg-pink-50 text-pink-700 flex items-center justify-center shrink-0"><Cog className="h-4.5 w-4.5" /></div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">Table Op CPM</div>
                {!isAdmin && <span className="text-[9.5px] uppercase tracking-wider text-slate-400 font-mono">Master</span>}
              </div>
              <input
                type="number" step="0.01"
                value={cpmTable}
                disabled={!canEdit || !isAdmin}
                onChange={(e) => setCpmTable(Number(e.target.value))}
                data-testid="cpm-table-input"
                className="mt-1 w-full font-mono font-bold text-lg outline-none bg-transparent focus:bg-slate-50 rounded px-1 py-0.5 disabled:cursor-not-allowed"
              />
              <div className="text-[10.5px] text-slate-500">₹/min · {isAdmin ? "editable by admin" : "read-only · admin managed"}</div>
            </div>
          </div>
        </Card>
        <Card className="p-4 rounded-md border-emerald-200 bg-emerald-50" data-testid="total-sam-card">
          <div className="text-[10.5px] uppercase tracking-wider text-emerald-800 font-semibold">Total SAM</div>
          <div className="mt-1 font-mono font-bold text-2xl text-emerald-800">{(totals.total_sam / 60).toFixed(2)}</div>
          <div className="text-[10.5px] text-emerald-700/80">Σ CT (Sec) ÷ 60 = min</div>
        </Card>
        <Card className="p-4 rounded-md border-emerald-200 bg-emerald-50" data-testid="total-sam-cost-card">
          <div className="text-[10.5px] uppercase tracking-wider text-emerald-800 font-semibold">Total SAM Cost</div>
          <div className="mt-1 font-mono font-bold text-2xl text-emerald-800">₹ {totals.sam_cost_total.toFixed(2)}</div>
          <div className="text-[10.5px] text-emerald-700/80">Σ (CT ÷ 60 × CPM)</div>
        </Card>
        <Card className="p-4 rounded-md border-pink-200 bg-pink-50" data-testid="part-rate-total-card">
          <div className="text-[10.5px] uppercase tracking-wider text-pink-800 font-semibold">Part Rate Based Costing</div>
          <div className="mt-1 font-mono font-bold text-2xl text-pink-800">₹ {totals.op_cost_total.toFixed(2)}</div>
          <div className="text-[10.5px] text-pink-700/80">Σ (No. Ops × Price/Op) · independent</div>
        </Card>
      </div>

      <Tabs defaultValue="operations" className="space-y-4">
        <TabsList>
          <TabsTrigger value="operations" data-testid="tab-operations">Operations</TabsTrigger>
          <TabsTrigger value="versions" data-testid="tab-versions">Version History</TabsTrigger>
        </TabsList>

        <TabsContent value="operations">
          <Card className="border-slate-200 rounded-md bg-white overflow-hidden">
            <div className="p-4 flex items-center justify-between border-b border-slate-100">
              <div>
                <div className="font-heading font-semibold text-[15px]">Operation Bulletin</div>
                <div className="text-[12px] text-slate-500">Green columns = CT-based costing · Pink columns = per-operation costing · Orange column = optional tools/guides · Drag rows to reorder</div>
              </div>
              {canEdit && <Button size="sm" variant="outline" onClick={addOp} data-testid="add-operation-btn"><Plus className="h-4 w-4 mr-1" /> Add operation</Button>}
            </div>
            <div className="overflow-x-auto">
              <table className="opsiq-table opsiq-bulletin-table">
                <thead>
                  <tr>
                    <th className="bg-emerald-50 text-emerald-800" style={{ width: 60 }}>S.No.</th>
                    <th className="bg-emerald-50 text-emerald-800" style={{ minWidth: 200 }}>Machine Used</th>
                    <th className="bg-emerald-50 text-emerald-800" style={{ minWidth: 240 }}>Operation Name</th>
                    <th className="bg-emerald-50 text-emerald-800 text-right" style={{ width: 90 }}>CT (Sec)</th>
                    <th className="bg-emerald-50 text-emerald-800 text-right" style={{ width: 130 }}>CT Cost (₹)</th>
                    <th className="bg-pink-50 text-pink-800 text-right" style={{ width: 100 }}># of Ops</th>
                    <th className="bg-pink-50 text-pink-800 text-right" style={{ width: 130 }}>Price / Op (₹)</th>
                    <th className="bg-pink-50 text-pink-800 text-right" style={{ width: 140 }}>Op Total (₹)</th>
                    <th className="bg-orange-50 text-orange-800" style={{ minWidth: 180 }}>Tools / Guides</th>
                    <th style={{ width: 96 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {ops.map((o, i) => {
                    const c = computeRow(o, cpmMachine, cpmTable);
                    const validOps = machineOperations(o.machine);
                    const isDragging = dragIdx === i;
                    const isDropTarget = dragOverIdx === i && dragIdx !== null && dragIdx !== i;
                    return (
                      <tr
                        key={i}
                        draggable={canEdit}
                        onDragStart={onDragStart(i)}
                        onDragOver={onDragOver(i)}
                        onDrop={onDrop(i)}
                        onDragEnd={onDragEnd}
                        data-testid={`op-row-${i}`}
                        className={`transition-colors ${isDragging ? "opacity-40" : ""} ${isDropTarget ? "outline outline-2 outline-brand outline-offset-[-2px]" : ""}`}
                      >
                        <td className="font-mono font-semibold text-emerald-700 bg-emerald-50/40">
                          <div className="flex items-center gap-1">
                            {canEdit && (
                              <button
                                type="button"
                                data-testid={`op-drag-${i}`}
                                className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-700 h-5 w-5 flex items-center justify-center"
                                title="Drag to reorder"
                              >
                                <GripVertical className="h-4 w-4" />
                              </button>
                            )}
                            <span>{o.seq}</span>
                          </div>
                        </td>
                        <td className="bg-emerald-50/40">
                          <Combobox
                            value={o.machine}
                            onChange={(v) => patchOp(i, { machine: v })}
                            options={machines.map((m) => m.name)}
                            placeholder="Select machine"
                            disabled={!canEdit}
                            testid={`op-machine-${i}`}
                            emptyText="No machine matches."
                          />
                        </td>
                        <td className="bg-emerald-50/40">
                          <Combobox
                            value={o.operation_name}
                            onChange={(v) => patchOp(i, { operation_name: v })}
                            options={validOps}
                            placeholder={o.machine ? "Select operation" : "Select machine first"}
                            disabled={!canEdit || !o.machine}
                            testid={`op-name-${i}`}
                            emptyText={o.machine ? "No operation matches." : "Select a machine first."}
                            onAddNew={o.machine ? async (newOp) => addOpToMachine(i, o.machine, newOp) : undefined}
                            addNewLabel="Add new operation"
                          />
                        </td>
                        <td className="bg-emerald-50/40">
                          <input
                            type="number" step="0.01" min={0}
                            disabled={!canEdit}
                            value={o.sam || 0}
                            onChange={(e) => patchOp(i, { sam: Number(e.target.value) })}
                            data-testid={`op-sam-${i}`}
                            className="w-full h-8 px-2 rounded border border-transparent hover:border-emerald-200 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-100 bg-transparent focus:bg-white text-[13px] outline-none text-right font-mono disabled:cursor-not-allowed"
                          />
                        </td>
                        <td className="bg-emerald-50/40 text-right font-mono font-semibold text-emerald-800">
                          {c.sam_cost.toFixed(2)}
                          <div className="text-[10px] text-emerald-600/70 font-normal">
                            × {((o.machine === "Table Operation" || o.machine === "Table Operator") ? cpmTable : cpmMachine).toString()}
                          </div>
                        </td>
                        <td className="bg-pink-50/40">
                          <input
                            type="number" min={0}
                            disabled={!canEdit}
                            value={o.num_operations || 0}
                            onChange={(e) => patchOp(i, { num_operations: Number(e.target.value) })}
                            data-testid={`op-num-${i}`}
                            className="w-full h-8 px-2 rounded border border-transparent hover:border-pink-200 focus:border-pink-500 focus:ring-1 focus:ring-pink-100 bg-transparent focus:bg-white text-[13px] outline-none text-right font-mono disabled:cursor-not-allowed"
                          />
                        </td>
                        <td className="bg-pink-50/40">
                          <input
                            type="number" step="0.01" min={0}
                            disabled={!canEdit}
                            value={o.price_per_operation || 0}
                            onChange={(e) => patchOp(i, { price_per_operation: Number(e.target.value) })}
                            data-testid={`op-price-${i}`}
                            className="w-full h-8 px-2 rounded border border-transparent hover:border-pink-200 focus:border-pink-500 focus:ring-1 focus:ring-pink-100 bg-transparent focus:bg-white text-[13px] outline-none text-right font-mono disabled:cursor-not-allowed"
                          />
                        </td>
                        <td className="bg-pink-50/40 text-right font-mono font-semibold text-pink-800">
                          {c.op_cost.toFixed(2)}
                        </td>
                        <td className="bg-orange-50/40">
                          <input
                            type="text"
                            disabled={!canEdit}
                            value={o.tools_guides || ""}
                            onChange={(e) => patchOp(i, { tools_guides: e.target.value })}
                            data-testid={`op-tools-${i}`}
                            placeholder="Optional"
                            className="w-full h-8 px-2 rounded border border-transparent hover:border-orange-200 focus:border-orange-500 focus:ring-1 focus:ring-orange-100 bg-transparent focus:bg-white text-[13px] text-orange-800 outline-none disabled:cursor-not-allowed placeholder:text-orange-300"
                          />
                        </td>
                        <td>
                          {canEdit && (
                            <div className="flex items-center gap-0.5 justify-end">
                              <button
                                onClick={() => removeOp(i)}
                                data-testid={`op-delete-${i}`}
                                className="h-7 w-7 rounded hover:bg-red-50 flex items-center justify-center text-slate-400 hover:text-red-600"
                                title="Delete row"
                              ><Trash2 className="h-4 w-4" /></button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {ops.length === 0 && (
                    <tr><td colSpan={10} className="text-center py-10 text-slate-500">No operations yet. Click "Add operation" to begin.</td></tr>
                  )}
                </tbody>
                {ops.length > 0 && (
                  <tfoot>
                    <tr>
                      <td colSpan={3} className="bg-emerald-100/60 text-right font-heading font-bold text-emerald-800">Total CT (Sec)</td>
                      <td className="bg-emerald-100/60 text-right font-mono font-bold text-emerald-800">{totals.total_sam.toFixed(2)}</td>
                      <td className="bg-emerald-100/60 text-right font-mono font-bold text-emerald-800">₹ {totals.sam_cost_total.toFixed(2)}</td>
                      <td colSpan={2} className="bg-pink-100/60 text-right font-heading font-bold text-pink-800">Part Rate Based Costing</td>
                      <td className="bg-pink-100/60 text-right font-mono font-bold text-pink-800">₹ {totals.op_cost_total.toFixed(2)}</td>
                      <td className="bg-orange-100/60"></td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="versions">
          <Card className="border-slate-200 rounded-md bg-white overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-heading font-semibold text-[15px]">Version History</div>
                <div className="text-[12px] text-slate-500">Each snapshot captures the operations, CPM rates and totals at that moment. Expand a row to see the diff.</div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="opsiq-table">
                <thead>
                  <tr>
                    <th style={{ width: 80 }}>Version</th>
                    <th style={{ width: 160 }}>Saved</th>
                    <th style={{ width: 180 }}>By</th>
                    <th className="text-right" style={{ width: 110 }}>CT Cost ₹</th>
                    <th className="text-right" style={{ width: 120 }}>Part-Rate ₹</th>
                    <th className="text-right" style={{ width: 100 }}>CPM M/T</th>
                    <th className="text-right" style={{ width: 90 }}>Ops</th>
                    <th className="text-right" style={{ width: 240 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Current live row */}
                  <tr className="bg-brand-soft/40">
                    <td className="font-mono font-bold text-brand">v{b.version || 1}</td>
                    <td className="text-[12.5px] text-slate-600">{(b.updated_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="text-[12.5px] text-slate-600">{b.updated_by || "—"}</td>
                    <td className="text-right font-mono text-[12.5px]">{Number(totals.sam_cost_total || 0).toFixed(2)}</td>
                    <td className="text-right font-mono text-[12.5px]">{Number(totals.op_cost_total || 0).toFixed(2)}</td>
                    <td className="text-right font-mono text-[12.5px]">{cpmMachine}/{cpmTable}</td>
                    <td className="text-right font-mono text-[12.5px]">{ops.length}</td>
                    <td className="text-right">
                      <span className="text-[10.5px] italic text-slate-500">Current (live)</span>
                    </td>
                  </tr>
                  {[...(b.versions || [])].sort((a, b) => (b.version || 0) - (a.version || 0)).map((v) => {
                    const isOpen = expandedVersion === v.version;
                    const liveSnap = { version: b.version, operations: ops, sam_cost_total: totals.sam_cost_total, op_cost_total: totals.op_cost_total, cpm_machine: cpmMachine, cpm_table: cpmTable };
                    const prevSnap = (b.versions || []).find((x) => (x.version || 0) === (v.version || 0) - 1);
                    const target = diffAgainst === "current" ? liveSnap : prevSnap;
                    return (
                      <Fragment key={v.version}>
                        <tr data-testid={`bulletin-version-${v.version}`} className={isOpen ? "bg-brand-soft/20" : ""}>
                          <td className="font-mono font-bold">v{v.version}</td>
                          <td className="text-[12.5px] text-slate-600">{(v.created_at || "").slice(0, 16).replace("T", " ")}</td>
                          <td className="text-[12.5px] text-slate-600">{v.created_by || "—"}</td>
                          <td className="text-right font-mono text-[12.5px]">{Number(v.sam_cost_total || 0).toFixed(2)}</td>
                          <td className="text-right font-mono text-[12.5px]">{Number(v.op_cost_total || 0).toFixed(2)}</td>
                          <td className="text-right font-mono text-[12.5px]">{v.cpm_machine ?? "?"}/{v.cpm_table ?? "?"}</td>
                          <td className="text-right font-mono text-[12.5px]">{(v.operations || []).length}</td>
                          <td className="text-right">
                            <div className="inline-flex items-center gap-1">
                              <Button size="sm" variant="outline" onClick={() => setExpandedVersion(isOpen ? null : v.version)} data-testid={`view-diff-v${v.version}`}>
                                {isOpen ? <ChevronDown className="h-3.5 w-3.5 mr-1" /> : <ChevronRight className="h-3.5 w-3.5 mr-1" />}
                                {isOpen ? "Hide diff" : "View diff"}
                              </Button>
                              {canEdit && (
                                <Button size="sm" variant="outline" onClick={() => restoreVersion(v.version)} data-testid={`restore-v${v.version}`}>
                                  <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {isOpen && (
                          <tr data-testid={`bulletin-version-diff-${v.version}`}>
                            <td colSpan={8} className="p-0">
                              <VersionDiffPanel
                                snapshot={v}
                                target={target}
                                targetLabel={diffAgainst === "current" ? `Current (v${b.version || 1})` : prevSnap ? `v${prevSnap.version}` : "No previous version"}
                                diffAgainst={diffAgainst}
                                setDiffAgainst={setDiffAgainst}
                                hasPrev={!!prevSnap}
                                tables={[{
                                  title: "Operations",
                                  cols: ["seq","machine","operation_name","sam","num_operations","price_per_operation","tools_guides"],
                                  colLabels: { seq: "#", machine: "Machine", operation_name: "Operation", sam: "CT (Sec)", num_operations: "# Ops", price_per_operation: "₹/Op", tools_guides: "Tools" },
                                  keys: ["machine","operation_name","sam","num_operations","price_per_operation","tools_guides"],
                                  snapRows: v.operations || [],
                                  targetRows: (target?.operations || []),
                                }]}
                                scalars={[{
                                  title: "Totals & CPM",
                                  snap: v,
                                  target: target || {},
                                  fields: [
                                    ["sam_cost_total", "Total SAM Cost", (x) => x === null || x === undefined ? "—" : `₹ ${Number(x).toFixed(2)}`],
                                    ["op_cost_total", "Total Part-Rate Cost", (x) => x === null || x === undefined ? "—" : `₹ ${Number(x).toFixed(2)}`],
                                    ["cpm_machine", "CPM (Machine)", (x) => x === null || x === undefined ? "—" : `₹ ${x}`],
                                    ["cpm_table", "CPM (Table)", (x) => x === null || x === undefined ? "—" : `₹ ${x}`],
                                  ],
                                }]}
                              />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                  {(b.versions || []).length === 0 && (
                    <tr>
                      <td colSpan={8} className="text-center py-8 text-slate-500 text-[13px]">
                        No prior versions. Once approved, click <span className="font-semibold">"New Version"</span> to save a snapshot.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Compare dialog (SAM cost comparison — primary metric) */}
      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Comparing v{compareVer?.version} → v{b.version} (SAM Cost)</DialogTitle></DialogHeader>
          <div className="max-h-96 overflow-y-auto">
            <table className="opsiq-table">
              <thead><tr><th>Machine</th><th>Operation</th><th className="text-right">v{compareVer?.version} SAM ₹</th><th className="text-right">v{b.version} SAM ₹</th><th className="text-right">Δ</th></tr></thead>
              <tbody>
                {comparisonRows.map((r, i) => {
                  const cls = r.diff > 0 ? "text-red-600" : r.diff < 0 ? "text-emerald-600" : "text-slate-400";
                  return (
                    <tr key={i} className={r.changed ? "bg-amber-50/40" : ""}>
                      <td className="text-slate-600 text-[12.5px]">{r.machine || "—"}</td>
                      <td>{r.name}</td>
                      <td className="text-right font-mono">{r.old != null ? `₹${r.old.toFixed(2)}` : "—"}</td>
                      <td className="text-right font-mono">{r.new != null ? `₹${r.new.toFixed(2)}` : "—"}</td>
                      <td className={`text-right font-mono ${cls}`}>{r.diff === 0 ? "—" : `${r.diff > 0 ? "+" : ""}${r.diff.toFixed(2)}`}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 font-semibold">
                  <td colSpan={2}>Total SAM Cost</td>
                  <td className="text-right font-mono">₹ {(compareVer?.sam_cost_total || 0).toFixed(2)}</td>
                  <td className="text-right font-mono">₹ {totals.sam_cost_total.toFixed(2)}</td>
                  <td className="text-right font-mono">{((totals.sam_cost_total - (compareVer?.sam_cost_total || 0)) >= 0 ? "+" : "")}{(totals.sam_cost_total - (compareVer?.sam_cost_total || 0)).toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* CPM change alert on new_version */}
      <Dialog open={cpmAlertOpen} onOpenChange={setCpmAlertOpen}>
        <DialogContent data-testid="cpm-alert-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700"><AlertTriangle className="h-5 w-5" /> Operator CPM has changed</DialogTitle>
          </DialogHeader>
          {cpmAlertData && (
            <div className="space-y-4">
              <p className="text-[13.5px] text-slate-700">The new version <span className="font-mono font-semibold">v{cpmAlertData.bulletin.version}</span> was created with the current Master CPM. These values differ from the prior version's rates — costs have been re-computed.</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-md border border-slate-200 p-3">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Machine Op CPM</div>
                  <div className="mt-1 font-mono text-[15px]">
                    <span className="text-slate-500 line-through">₹ {cpmAlertData.prior_cpm.cpm_machine}</span>
                    <span className="mx-2 text-slate-400">→</span>
                    <span className="text-emerald-700 font-bold">₹ {cpmAlertData.new_cpm.cpm_machine}</span>
                  </div>
                </div>
                <div className="rounded-md border border-slate-200 p-3">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Table Op CPM</div>
                  <div className="mt-1 font-mono text-[15px]">
                    <span className="text-slate-500 line-through">₹ {cpmAlertData.prior_cpm.cpm_table}</span>
                    <span className="mx-2 text-slate-400">→</span>
                    <span className="text-emerald-700 font-bold">₹ {cpmAlertData.new_cpm.cpm_table}</span>
                  </div>
                </div>
              </div>
              <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-[12.5px] text-amber-900">
                All SAM Costs in this version have been recalculated using the new rates. Previous approved versions remain unchanged.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setCpmAlertOpen(false)} className="bg-brand hover:bg-brand-dark text-white" data-testid="cpm-alert-ok">Got it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate as new style */}
      <Dialog open={dupOpen} onOpenChange={setDupOpen}>
        <DialogContent data-testid="duplicate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Copy className="h-5 w-5 text-brand" /> Duplicate as new style</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-[12.5px] text-slate-500">All operations and rows will be copied to a fresh draft (v1) using the current Master CPM rates. Version history is not carried over.</p>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">New Buyer <span className="text-rose-600">*</span></label>
              <Input value={dupForm.buyer} onChange={(e) => setDupForm({ ...dupForm, buyer: e.target.value })} placeholder="e.g. H&M" data-testid="dup-buyer" className="mt-1" />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">New Our Style Number <span className="text-rose-600">*</span></label>
              <Input value={dupForm.our_style} onChange={(e) => setDupForm({ ...dupForm, our_style: e.target.value })} placeholder="e.g. BBS-2026-045" data-testid="dup-our-style" className="mt-1" />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">New Buyer Style Number</label>
              <Input value={dupForm.buyer_style} onChange={(e) => setDupForm({ ...dupForm, buyer_style: e.target.value })} placeholder="Optional" data-testid="dup-buyer-style" className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDupOpen(false)}>Cancel</Button>
            <Button onClick={doDuplicate} disabled={dupSaving} className="bg-brand hover:bg-brand-dark text-white" data-testid="dup-confirm-btn">
              <Copy className="h-4 w-4 mr-2" /> {dupSaving ? "Duplicating…" : "Create duplicate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function computeCompare(oldOps, newOps, oldCpmM, oldCpmT, newCpmM, newCpmT) {
  const key = (o) => `${o.machine || "?"}::${o.operation_name || o.name || "?"}`;
  const map = new Map();
  oldOps.forEach((o) => {
    const c = computeRow(o, oldCpmM, oldCpmT);
    map.set(key(o), { machine: o.machine, name: o.operation_name || o.name || "?", old: c.sam_cost, new: null });
  });
  newOps.forEach((n) => {
    const k = key(n);
    const c = computeRow(n, newCpmM, newCpmT);
    const existing = map.get(k);
    if (existing) existing.new = c.sam_cost;
    else map.set(k, { machine: n.machine, name: n.operation_name || "?", old: null, new: c.sam_cost });
  });
  return Array.from(map.values()).map((r) => {
    const diff = (r.new || 0) - (r.old || 0);
    return { ...r, diff, changed: r.old !== r.new };
  });
}
